import { useState } from "react"

import type { QAReviewResult } from "./qa-review-dialog"

import {
  ClipboardCheck,
  ExternalLink,
  MessageSquareQuote,
  Presentation,
  RefreshCw,
  ShieldAlert,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

import { QAReviewDialog } from "./qa-review-dialog"
import { SalesPitchDialog } from "./sales-pitch-dialog"

function getCompactScoreTextColor(score: number): string {
  if (score >= 80) return "text-success"
  if (score >= 60) return "text-warning"
  return "text-destructive"
}

function getCompactScoreStrokeColor(score: number): string {
  if (score >= 80) return "stroke-success"
  if (score >= 60) return "stroke-warning"
  return "stroke-destructive"
}

function QAButtonContent({ isLoading, hasResult }: { isLoading: boolean; hasResult: boolean }) {
  if (isLoading) {
    return (
      <>
        <Spinner className="mr-1 size-3.5" />
        Analyzing...
      </>
    )
  }
  if (hasResult) {
    return (
      <>
        <RefreshCw className="mr-1 size-3.5" />
        Re-analyze
      </>
    )
  }
  return (
    <>
      <ClipboardCheck className="mr-1 size-3.5" />
      Run QA Check
    </>
  )
}

function PitchButtonContent({ isLoading, hasResult }: { isLoading: boolean; hasResult: boolean }) {
  if (isLoading) {
    return (
      <>
        <Spinner className="mr-1 size-3.5" />
        Generating...
      </>
    )
  }
  if (hasResult) {
    return (
      <>
        <RefreshCw className="mr-1 size-3.5" />
        Regenerate
      </>
    )
  }
  return (
    <>
      <Presentation className="mr-1 size-3.5" />
      Generate
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  QA Score Ring (compact for card)                                   */
/* ------------------------------------------------------------------ */

function ScoreRingCompact({ score, grade }: { score: number; grade: string }) {
  const radius = 36
  const stroke = 6
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference
  const size = 88

  const color = getCompactScoreTextColor(score)

  const strokeColor = getCompactScoreStrokeColor(score)

  return (
    <div className="relative flex shrink-0 items-center justify-center">
      <svg className="-rotate-90" height={size} width={size}>
        <circle
          className="stroke-muted"
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          strokeWidth={stroke}
        />
        <circle
          className={cn(strokeColor, "transition-[stroke,stroke-dashoffset] duration-700")}
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          strokeWidth={stroke}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-xl leading-none font-bold tabular-nums", color)}>{score}</span>
        <span className={cn("text-sm leading-none font-bold", color)}>{grade}</span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  QA Review Card                                                     */
/* ------------------------------------------------------------------ */

type QACardProps = {
  isLoading: boolean
  result: QAReviewResult | null
  onRun: () => void
}

function QAReviewCard({ isLoading, result, onRun }: QACardProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  const highCount = result?.issues.filter((i) => i.severity === "high").length ?? 0
  const medCount = result?.issues.filter((i) => i.severity === "medium").length ?? 0
  const lowCount = result?.issues.filter((i) => i.severity === "low").length ?? 0
  const totalIssues = highCount + medCount + lowCount

  return (
    <>
      <Card className="overflow-hidden rounded-xl">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <div className="flex items-center gap-2">
            <ShieldAlert className="size-4" />
            <h3 className="text-sm font-semibold">QA Review</h3>
          </div>
          <Button
            disabled={isLoading}
            size="sm"
            variant={result ? "ghost" : "outline"}
            onClick={onRun}
          >
            <QAButtonContent hasResult={!!result} isLoading={isLoading} />
          </Button>
        </div>

        <CardContent className="p-4">
          {isLoading && !result && (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <Spinner className="size-6" />
              <p className="text-muted-foreground text-xs">
                Checking completeness, consistency, and quality...
              </p>
            </div>
          )}
          {result && (
            <div className="space-y-3">
              {/* Score + Summary row */}
              <div className="flex gap-4">
                <ScoreRingCompact grade={result.grade} score={result.score} />
                <div className="flex-1">
                  <p className="line-clamp-3 text-xs leading-relaxed">{result.summary}</p>

                  {/* Issue severity pills */}
                  {totalIssues > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      {highCount > 0 && (
                        <span className="bg-destructive text-destructive-foreground border-foreground inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium">
                          {highCount} high
                        </span>
                      )}
                      {medCount > 0 && (
                        <span className="bg-warning text-warning-foreground border-foreground inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium">
                          {medCount} medium
                        </span>
                      )}
                      {lowCount > 0 && (
                        <span className="bg-accent text-accent-foreground border-foreground inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium">
                          {lowCount} low
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Show details button → opens modal */}
              {(totalIssues > 0 || result.suggestions.length > 0) && (
                <button
                  className="text-muted-foreground hover:text-foreground/80 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed py-1.5 text-xs font-medium transition-colors hover:border-solid"
                  onClick={() => setDialogOpen(true)}
                >
                  <ExternalLink className="size-3" />
                  Show details ({totalIssues} issues
                  {result.suggestions.length > 0
                    ? `, ${result.suggestions.length} suggestions`
                    : ""}
                  )
                </button>
              )}
            </div>
          )}
          {!isLoading && !result && (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-center">
              <ShieldAlert className="text-muted-foreground/40 size-8" />
              <p className="text-xs">
                Run a QA check to analyze completeness, consistency, and quality of this use case.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QA Detail Modal */}
      <QAReviewDialog
        isLoading={isLoading}
        open={dialogOpen}
        result={result}
        onOpenChange={setDialogOpen}
        onRegenerate={onRun}
      />
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Sales Pitch Card                                                   */
/* ------------------------------------------------------------------ */

const sectionLabels = ["The Challenge", "The Solution", "Business Impact", "Why Now"]
const sectionColors = [
  "border-l-destructive bg-destructive/10",
  "border-l-success bg-success/10",
  "border-l-primary bg-primary/10",
  "border-l-warning bg-warning/10",
]

function PitchPreview({ text }: { text: string }) {
  const paragraphs = text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)

  // Show first 2 paragraphs as preview
  const preview = paragraphs.slice(0, 2)

  if (preview.length === 0) {
    return <p className="text-xs leading-relaxed">{text.slice(0, 200)}</p>
  }

  return (
    <div className="space-y-2">
      {preview.map((p, i) => {
        const label = sectionLabels[i] ?? null
        const color = sectionColors[i % sectionColors.length]
        return (
          <div key={i} className={cn("rounded-md border-l-[3px] px-3 py-2", color)}>
            {label && (
              <span className="text-muted-foreground mb-0.5 block text-[10px] font-bold tracking-wider uppercase">
                {label}
              </span>
            )}
            <p className="line-clamp-2 text-xs leading-relaxed">{p}</p>
          </div>
        )
      })}
      {paragraphs.length > 2 && (
        <p className="text-muted-foreground text-center text-[10px]">
          +{paragraphs.length - 2} more sections
        </p>
      )}
    </div>
  )
}

type SalesPitchCardProps = {
  isLoading: boolean
  result: string | null
  onRun: () => void
}

function SalesPitchCard({ isLoading, result, onRun }: SalesPitchCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <>
      <Card className="overflow-hidden rounded-xl">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Presentation className="size-4" />
            <h3 className="text-sm font-semibold">Sales Pitch</h3>
          </div>
          <Button
            disabled={isLoading}
            size="sm"
            variant={result ? "ghost" : "outline"}
            onClick={onRun}
          >
            <PitchButtonContent hasResult={!!result} isLoading={isLoading} />
          </Button>
        </div>

        <CardContent className="p-4">
          {isLoading && !result && (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <Spinner className="size-6" />
              <p className="text-muted-foreground text-xs">Crafting a compelling pitch...</p>
            </div>
          )}
          {result && (
            <div className="space-y-3">
              {/* Quote accent */}
              <div className="flex items-start gap-2">
                <MessageSquareQuote className="text-muted-foreground/30 mt-0.5 size-4 shrink-0" />
                <p className="text-muted-foreground text-[10px] italic">
                  AI-generated pitch based on your use case data
                </p>
              </div>

              {/* Preview of first 2 paragraphs */}
              <PitchPreview text={result} />

              {/* Show full pitch button → opens modal */}
              <button
                className="text-muted-foreground hover:text-foreground flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed py-1.5 text-xs font-medium transition-colors hover:border-solid"
                onClick={() => setDialogOpen(true)}
              >
                <ExternalLink className="size-3" />
                View full pitch
              </button>
            </div>
          )}
          {!isLoading && !result && (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-center">
              <Presentation className="text-muted-foreground/40 size-8" />
              <p className="text-xs">
                Generate a persuasive sales pitch based on this use case's problem, solution, and
                ROI data.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sales Pitch Detail Modal */}
      <SalesPitchDialog
        isLoading={isLoading}
        open={dialogOpen}
        result={result}
        onOpenChange={setDialogOpen}
        onRegenerate={onRun}
      />
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Combined export                                                    */
/* ------------------------------------------------------------------ */

type FactSheetAICardsProps = {
  qaIsLoading: boolean
  qaResult: QAReviewResult | null
  onRunQa?: () => void
  salesPitchIsLoading: boolean
  salesPitchResult: string | null
  onRunSalesPitch: () => void
}

export function FactSheetAICards({
  qaIsLoading,
  qaResult,
  onRunQa,
  salesPitchIsLoading,
  salesPitchResult,
  onRunSalesPitch,
}: FactSheetAICardsProps) {
  const showQa = !!onRunQa

  return (
    <div className={showQa ? "grid grid-cols-2 gap-3" : ""}>
      {showQa && <QAReviewCard isLoading={qaIsLoading} result={qaResult} onRun={onRunQa} />}
      <SalesPitchCard
        isLoading={salesPitchIsLoading}
        result={salesPitchResult}
        onRun={onRunSalesPitch}
      />
    </div>
  )
}
